"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import Link from "next/link";
import type { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
interface SidebarNavItemProps {
  icon: ReactNode;
  label: string;
  href?: string;
  active?: boolean;
  badge?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  dataGuide?: string;
  layoutId?: string;
  collapsed?: boolean;
}
function SidebarNavItem(t0) {
  const $ = _c(49);
  const {
    icon,
    label,
    href,
    active,
    badge,
    trailing,
    onClick,
    dataGuide,
    layoutId,
    collapsed
  } = t0;
  const t1 = collapsed ? "justify-center px-2 py-2 text-zinc-500 dark:text-zinc-300" : "gap-3 px-3 py-2 text-ds-text-subtle";
  const t2 = !active && (collapsed ? "hover:bg-white/60 dark:hover:bg-zinc-800/50" : "hover:bg-ds-surface-hover border border-transparent");
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("group relative flex w-full items-center text-left rounded-lg text-sm font-normal leading-tight transition-[background-color,border-color,box-shadow,color] duration-150 ease-out", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const className = t3;
  let t4;
  if ($[3] !== active || $[4] !== collapsed || $[5] !== layoutId) {
    t4 = active && layoutId ? <motion.span layoutId={layoutId} className={cn("shadow-ds-soft-pressed absolute inset-0 rounded-lg", collapsed ? "bg-ds-surface-card" : "border-ds-border-default bg-ds-surface-card border")} transition={{
      type: "spring",
      stiffness: 350,
      damping: 30
    }} /> : active ? <span className={cn("shadow-ds-soft-pressed absolute inset-0 rounded-lg", collapsed ? "bg-ds-surface-card" : "border-ds-border-default bg-ds-surface-card border")} /> : null;
    $[3] = active;
    $[4] = collapsed;
    $[5] = layoutId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== icon) {
    t5 = <span className="relative z-10 flex size-6 shrink-0 items-center justify-center">{icon}</span>;
    $[7] = icon;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== collapsed || $[10] !== label) {
    t6 = !collapsed && <span className="relative z-10 min-w-0 flex-1 truncate font-medium">{label}</span>;
    $[9] = collapsed;
    $[10] = label;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== badge || $[13] !== collapsed) {
    t7 = !collapsed && badge && <span className="relative z-10">{badge}</span>;
    $[12] = badge;
    $[13] = collapsed;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== collapsed || $[16] !== trailing) {
    t8 = !collapsed && trailing && <span className="relative z-10">{trailing}</span>;
    $[15] = collapsed;
    $[16] = trailing;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t9 = <>{t4}{t5}{t6}{t7}{t8}</>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const content = t9;
  let element;
  if (href) {
    const t10 = collapsed ? label : undefined;
    let t11;
    if ($[24] !== className || $[25] !== content || $[26] !== dataGuide || $[27] !== href || $[28] !== onClick || $[29] !== t10) {
      t11 = <Link href={href} prefetch={false} onClick={onClick} className={className} data-guide={dataGuide} aria-label={t10}>{content}</Link>;
      $[24] = className;
      $[25] = content;
      $[26] = dataGuide;
      $[27] = href;
      $[28] = onClick;
      $[29] = t10;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    element = t11;
  } else {
    if (!onClick) {
      const t10 = collapsed ? label : undefined;
      let t11;
      if ($[31] !== className || $[32] !== content || $[33] !== dataGuide || $[34] !== t10) {
        t11 = <div className={className} data-guide={dataGuide} aria-label={t10}>{content}</div>;
        $[31] = className;
        $[32] = content;
        $[33] = dataGuide;
        $[34] = t10;
        $[35] = t11;
      } else {
        t11 = $[35];
      }
      element = t11;
    } else {
      const t10 = collapsed ? label : undefined;
      let t11;
      if ($[36] !== className || $[37] !== content || $[38] !== dataGuide || $[39] !== onClick || $[40] !== t10) {
        t11 = <button type="button" onClick={onClick} className={className} data-guide={dataGuide} aria-label={t10}>{content}</button>;
        $[36] = className;
        $[37] = content;
        $[38] = dataGuide;
        $[39] = onClick;
        $[40] = t10;
        $[41] = t11;
      } else {
        t11 = $[41];
      }
      element = t11;
    }
  }
  if (!collapsed) {
    return element;
  }
  let t10;
  if ($[42] !== element) {
    t10 = <TooltipTrigger asChild={true}>{element}</TooltipTrigger>;
    $[42] = element;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== label) {
    t11 = <TooltipContent side="right" sideOffset={8}>{label}</TooltipContent>;
    $[44] = label;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t10 || $[47] !== t11) {
    t12 = <Tooltip>{t10}{t11}</Tooltip>;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  return t12;
}
export { SidebarNavItem };
export type { SidebarNavItemProps };